import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import CallButton from "../components/CallButton";
import { ASSETS, BRAND, buildWhatsAppLink } from "../lib/api";
import { Phone, Mail, MessageCircle, MapPin, Clock } from "lucide-react";

const SUPPORT_EMAIL = "email@bonsevis.com";

const HOURS = [
  { day: "Monday — Friday", time: "8:00 AM — 7:00 PM EST" },
  { day: "Saturday", time: "9:00 AM — 4:00 PM EST" },
  { day: "Sunday", time: "Closed (WhatsApp 24/7)" },
];

const POSTS = [
  { city: "Miami, Florida", address: "8200 NW 27th St, Doral, FL 33122" },
  { city: "Montréal, Québec", address: "9500 Boulevard St-Laurent, Montréal QC" },
  { city: "Santiago, R.D.", address: "Av. 27 de Febrero, Santiago de los Caballeros" },
  { city: "Port-au-Prince, Haïti", address: "Boulevard Toussaint Louverture, Tabarre" },
];

export default function Contact() {
  return (
    <div data-testid="contact-page" className="min-h-screen flex flex-col" style={{ background: "#F5EFE0" }}>
      <Navbar />

      {/* Hero band */}
      <section className="relative" style={{ background: "#0F1D14", color: "#F5EFE0" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3 ornament">
              Contact &amp; Inquiry
            </div>
            <h1 className="font-serif-bv text-5xl lg:text-6xl text-[#F5EFE0] leading-[1.05]">
              We&apos;re <span className="italic text-[#D4AF37]">here</span> when you need us.
            </h1>
            <p className="mt-6 text-[#F5EFE0]/85 max-w-2xl text-lg leading-relaxed">
              Reach Hayti Bon Sèvis Shipping by phone, WhatsApp, or email. Our agents handle inquiries in Kreyòl, French, English, Spanish, and Portuguese.
            </p>
          </div>
          <div className="lg:col-span-5 flex justify-center">
            <img
              src={ASSETS.logo}
              alt="Hayti Bon Sèvis Shipping"
              className="bg-white rounded-sm p-6 max-h-[280px] w-auto"
            />
          </div>
        </div>
      </section>

      {/* Channels */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-3 gap-6">
        <a
          href={`tel:${BRAND.phone_e164}`}
          data-testid="contact-phone-card"
          className="group p-8 bg-white border border-[#1B3022]/15 rounded-sm hover:border-[#D4AF37] transition-colors"
        >
          <Phone className="text-[#D4AF37]" size={28} />
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#1B3022]/60 mt-4">Phone</div>
          <div className="font-serif-bv text-3xl text-[#1B3022] mt-2 tabular">{BRAND.phone}</div>
          <p className="text-sm text-[#1B3022]/65 mt-3">
            Tap to call. US, Canada &amp; Haiti customer support line.
          </p>
          <div className="mt-5 text-xs tracking-widest uppercase text-[#A88420] group-hover:text-[#D4AF37]">Call now →</div>
        </a>

        <a
          href={buildWhatsAppLink()}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-whatsapp-card"
          className="group p-8 border border-[#25D366] rounded-sm hover:bg-[#25D366]/5 transition-colors bg-white"
        >
          <MessageCircle className="text-[#25D366]" size={28} />
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#1B3022]/60 mt-4">WhatsApp</div>
          <div className="font-serif-bv text-3xl text-[#1B3022] mt-2 tabular">{BRAND.phone}</div>
          <p className="text-sm text-[#1B3022]/65 mt-3">
            Chat 24/7 — fastest way to reach an agent. Send photos, questions, or tracking IDs.
          </p>
          <div className="mt-5 text-xs tracking-widest uppercase text-[#25D366]">Open chat →</div>
        </a>

        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          data-testid="contact-email-card"
          className="group p-8 bg-white border border-[#1B3022]/15 rounded-sm hover:border-[#D4AF37] transition-colors"
        >
          <Mail className="text-[#D4AF37]" size={28} />
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#1B3022]/60 mt-4">Email</div>
          <div className="font-serif-bv text-2xl text-[#1B3022] mt-2 break-all">{SUPPORT_EMAIL}</div>
          <p className="text-sm text-[#1B3022]/65 mt-3">
            For commercial accounts, claims, and formal correspondence. Response within 24h.
          </p>
          <div className="mt-5 text-xs tracking-widest uppercase text-[#A88420] group-hover:text-[#D4AF37]">Compose →</div>
        </a>
      </section>

      {/* Hours + Posts */}
      <section className="bg-[#1B3022] text-[#F5EFE0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3 ornament">Office Hours</div>
            <h2 className="font-serif-bv text-3xl lg:text-4xl text-[#F5EFE0]">When we answer.</h2>
            <div className="mt-6 space-y-3">
              {HOURS.map((h) => (
                <div key={h.day} className="flex justify-between items-center border-b border-[#D4AF37]/20 pb-3 max-w-md">
                  <span className="flex items-center gap-2 text-[#F5EFE0]/85">
                    <Clock size={14} className="text-[#D4AF37]" />
                    {h.day}
                  </span>
                  <span className="font-serif-bv text-[#D4AF37]">{h.time}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-[#F5EFE0]/70 max-w-md italic">
              Outside office hours, WhatsApp delivers automated tracking updates and routes urgent issues to the on-call agent.
            </p>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3 ornament">Trading Posts</div>
            <h2 className="font-serif-bv text-3xl lg:text-4xl text-[#F5EFE0]">Walk in to any office.</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {POSTS.map((p) => (
                <div key={p.city} className="border border-[#D4AF37]/30 p-4 rounded-sm bg-[#0F1D14]/50">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin size={14} className="text-[#D4AF37]" />
                    <span className="font-serif-bv text-lg text-[#D4AF37]">{p.city}</span>
                  </div>
                  <p className="text-xs text-[#F5EFE0]/70">{p.address}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <CallButton testid="contact-call-fab" />
      <WhatsAppButton variant="floating" testid="contact-whatsapp-fab" />
    </div>
  );
}
